// ═══════════════════════════════════════════════════════════════
// MEITHO BGV — INTERACTIVE DEMO PAGE
// meitho.com/demo — No login required
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from "react";

const FF = "'Inter','Segoe UI',sans-serif";
const FFD = "'DM Sans','Inter',sans-serif";
const FFM = "'JetBrains Mono','Fira Code',monospace";

// ── Demo Data ─────────────────────────────────────────────────
const DEMO_PROFILES = {
  uan: {
    id: "101257351470",
    label: "UAN Search",
    icon: "🏦",
    delay: 1800,
    result: {
      name: "RAJESH KUMAR",
      uan: "101257351470",
      dob: "1990-05-15",
      gender: "Male",
      employmentHistory: [
        { name: "RAJESH KUMAR", company: "INFOSYS LIMITED", doj: "2021-03-01", dor: "Currently Employed", designation: "Senior Engineer", memberId: "TNMAS00541270000011230" },
        { name: "RAJESH KUMAR", company: "WIPRO TECHNOLOGIES", doj: "2018-06-15", dor: "2021-02-28", designation: "Software Engineer", memberId: "TNAMB00399690000011582" },
        { name: "RAJESH KUMAR", company: "TCS LIMITED", doj: "2015-07-20", dor: "2018-05-30", designation: "Junior Developer", memberId: "TBTAM00404240000015027" },
      ],
      meithoScore: 78,
      grade: "Trusted",
    }
  },
  pan: {
    id: "AQLPJ2746D",
    label: "PAN Verify",
    icon: "📋",
    delay: 1200,
    result: {
      name: "RAJESH KUMAR",
      pan: "AQLPJ2746D",
      dob: "1990-05-15",
      gender: "M",
      category: "person",
      status: "valid",
      aadhaar_linked: true,
      masked_aadhaar: "XXXXXXXX4521",
      address: { line_1: "42, MG Road", city: "Chennai", state: "Tamil Nadu", zip: "600001", full: "42, MG Road, Chennai, Tamil Nadu - 600001" }
    }
  },
  dl: {
    id: "TN2020150000123",
    label: "Driving License",
    icon: "🚗",
    delay: 1500,
    result: {
      name: "RAJESH KUMAR",
      dl_number: "TN2020150000123",
      dob: "1990-05-15",
      doi: "2015-06-10",
      doe: "2035-05-14",
      state: "Tamil Nadu",
      gender: "Male",
      father_name: "SURESH KUMAR",
      blood_group: "O+",
      permanent_address: "42, MG Road, Chennai, Tamil Nadu - 600001",
      vehicle_classes: ["MCWG", "LMV"],
      ola_name: "DISTRICT TRANSPORT OFFICE, CHENNAI",
      isExpired: false,
      has_image: true,
    }
  },
  fullbgv: {
    id: "FULL BGV",
    label: "Full BGV Check",
    icon: "⚡",
    delay: 3000,
    result: {
      name: "RAJESH KUMAR",
      uan: "101257351470",
      pan: "AQLPJ2746D",
      meithoScore: 78,
      grade: "Trusted",
      color: "#0cc0df",
      checks: [
        { label: "UAN / EPFO", icon: "🏦", status: "verified", detail: "3 employers · 9+ years" },
        { label: "PAN Identity", icon: "📋", status: "verified", detail: "Valid · Aadhaar linked" },
        { label: "Driving License", icon: "🚗", status: "verified", detail: "Valid till 2035" },
      ],
      breakdown: { identity: 22, stability: 20, experience: 22, continuity: 14 },
      badges: ["✅ Senior Professional", "🏅 Loyal Employee", "🔗 No Career Gaps"],
      employment: [
        { company: "INFOSYS LIMITED", role: "Senior Engineer", from: "Mar 2021", to: "Present", years: "3+ yrs" },
        { company: "WIPRO TECHNOLOGIES", role: "Software Engineer", from: "Jun 2018", to: "Feb 2021", years: "2.7 yrs" },
        { company: "TCS LIMITED", role: "Junior Developer", from: "Jul 2015", to: "May 2018", years: "2.8 yrs" },
      ]
    }
  },
  refcheck: {
    id: "REF CHECK",
    label: "Reference Check",
    icon: "📧",
    delay: 2500,
    result: {
      name: "RAJESH KUMAR",
      ref1: { name: "Priya Sharma", role: "HR Manager", company: "Infosys", behaviour: 5, ethical: 4, professional: 5, rehire: "Yes", comment: "Excellent team player, always delivers on time." },
      ref2: { name: "Arun Mehta", role: "Tech Lead", company: "Wipro", behaviour: 4, ethical: 5, professional: 4, rehire: "Yes", comment: "Strong technical skills and great work ethic." },
      verifications: {
        company: "true", designation: "true", doj: "true", dor: "partial",
        lastSalary: "true", noticePeriod: "true"
      },
      overallScore: 4.5,
    }
  }
};

// ── UI Components ─────────────────────────────────────────────
function Badge({ children, color = "#0cc0df" }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: color + "18", border: `1px solid ${color}30`, color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children}
    </span>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "9px 12px" }}>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: mono ? FFM : FF }}>{value || "—"}</div>
    </div>
  );
}

function Star({ filled }) {
  return <span style={{ color: filled ? "#ff914d" : "rgba(255,255,255,0.15)", fontSize: 14 }}>★</span>;
}

function ScoreRing({ score, max = 100, color = "#0cc0df", size = 100 }) {
  const r = size / 2 - 10;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${(score/max)*circ} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: "stroke-dasharray 1s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: FFD, fontSize: size / 5, fontWeight: 900, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: size / 12, color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>/{max}</div>
      </div>
    </div>
  );
}

// ── Result Displays ───────────────────────────────────────────
function UANResult({ data }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, padding: "14px 16px", background: "rgba(16,185,129,0.08)", borderRadius: 12, border: "1px solid rgba(16,185,129,0.2)" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#10b981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>👤</div>
        <div>
          <div style={{ fontFamily: FFD, fontSize: 20, fontWeight: 800, color: "#fff" }}>{data.name}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: FFM }}>{data.uan}</div>
        </div>
        <div style={{ marginLeft: "auto" }}><Badge color="#10b981">✅ EPFO Verified</Badge></div>
      </div>
      <div style={{ fontSize: 11, color: "#0cc0df", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Employment History</div>
      {data.employmentHistory.map((e, i) => (
        <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "12px 14px", marginBottom: 8, border: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: i === 0 ? "#10b981" : "rgba(255,255,255,0.2)", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{e.company}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{e.doj} → {e.dor}</div>
          </div>
          {i === 0 && <Badge color="#10b981">Current</Badge>}
        </div>
      ))}
    </div>
  );
}

function PANResult({ data }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, padding: "14px 16px", background: "rgba(12,192,223,0.08)", borderRadius: 12, border: "1px solid rgba(12,192,223,0.2)" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#0cc0df,#0891b2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📋</div>
        <div>
          <div style={{ fontFamily: FFD, fontSize: 20, fontWeight: 800, color: "#fff" }}>{data.name}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: FFM }}>{data.pan}</div>
        </div>
        <div style={{ marginLeft: "auto" }}><Badge color="#0cc0df">✅ Valid PAN</Badge></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <InfoRow label="Full Name" value={data.name} />
        <InfoRow label="Date of Birth" value={data.dob} />
        <InfoRow label="Gender" value={data.gender === "M" ? "Male" : "Female"} />
        <InfoRow label="Category" value={data.category} />
        <InfoRow label="Status" value={data.status?.toUpperCase()} />
        <InfoRow label="Masked Aadhaar" value={data.masked_aadhaar} mono />
      </div>
      <div style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#10b981", fontWeight: 600 }}>
        ✅ Aadhaar linked to this PAN
      </div>
      {data.address?.full && (
        <div style={{ marginTop: 10, background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>Address</div>
          <div style={{ fontSize: 13, color: "#fff" }}>{data.address.full}</div>
        </div>
      )}
    </div>
  );
}

function DLResult({ data }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, padding: "14px 16px", background: "rgba(255,145,77,0.08)", borderRadius: 12, border: "1px solid rgba(255,145,77,0.2)" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#ff914d,#f97316)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🚗</div>
        <div>
          <div style={{ fontFamily: FFD, fontSize: 20, fontWeight: 800, color: "#fff" }}>{data.name}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: FFM }}>{data.dl_number}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexDirection: "column", alignItems: "flex-end" }}>
          <Badge color="#10b981">✅ Valid DL</Badge>
          <Badge color="#ef4444">🩸 {data.blood_group}</Badge>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <InfoRow label="DL Number" value={data.dl_number} mono />
        <InfoRow label="State" value={data.state} />
        <InfoRow label="Date of Issue" value={data.doi} />
        <InfoRow label="Expiry Date" value={data.doe} />
        <InfoRow label="Father / Spouse" value={data.father_name} />
        <InfoRow label="Issuing RTO" value={data.ola_name} />
      </div>
      <InfoRow label="Registered Address" value={data.permanent_address} />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Authorised Vehicle Classes</div>
        <div style={{ display: "flex", gap: 8 }}>
          {data.vehicle_classes.map((vc, i) => <Badge key={i} color="#ff914d">🚗 {vc}</Badge>)}
        </div>
      </div>
    </div>
  );
}

function FullBGVResult({ data }) {
  const color = data.score >= 85 ? "#10b981" : data.score >= 65 ? "#0cc0df" : "#ff914d";
  return (
    <div>
      {/* Score */}
      <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: 20, marginBottom: 16, border: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: 20, alignItems: "center" }}>
        <ScoreRing score={data.meithoScore} color="#0cc0df" size={100} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 4 }}>MEITHO SCORE</div>
          <div style={{ fontFamily: FFD, fontSize: 22, fontWeight: 800, color: "#0cc0df", marginBottom: 4 }}>{data.grade}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {data.badges.map((b, i) => <Badge key={i} color="#0cc0df">{b}</Badge>)}
          </div>
        </div>
      </div>

      {/* Score breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
        {[["🪪 Identity", data.breakdown.identity, 25], ["⏱ Stability", data.breakdown.stability, 25], ["📈 Experience", data.breakdown.experience, 25], ["🔗 Continuity", data.breakdown.continuity, 25]].map(([l, v, max]) => (
          <div key={l} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{l}</div>
              <div style={{ fontFamily: FFD, fontSize: 12, fontWeight: 700, color: "#0cc0df" }}>{v}/{max}</div>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <div style={{ height: "100%", width: `${(v/max)*100}%`, background: "#0cc0df", borderRadius: 2 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Checks summary */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Verification Summary</div>
        {data.checks.map((c, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 18 }}>{c.icon}</span>
              <div>
                <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{c.label}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{c.detail}</div>
              </div>
            </div>
            <Badge color="#10b981">✓ {c.status}</Badge>
          </div>
        ))}
      </div>

      {/* Employment */}
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Employment Timeline</div>
      {data.employment.map((e, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 8, padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: i === 0 ? "#10b981" : "#0cc0df", marginTop: 5, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{e.company}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{e.role} · {e.from} – {e.to}</div>
          </div>
          <div style={{ fontSize: 11, color: "#0cc0df", fontWeight: 600 }}>{e.years}</div>
        </div>
      ))}
    </div>
  );
}

function RefCheckResult({ data }) {
  const stars = (n) => [1,2,3,4,5].map(i => <Star key={i} filled={i <= n} />);
  const verdict = (v) => v === "true" ? <Badge color="#10b981">✓ True</Badge> : v === "false" ? <Badge color="#ef4444">✗ False</Badge> : <Badge color="#f59e0b">~ Partial</Badge>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Candidate Claims — Verified</div>
        {[["Company", "verifyCompany"], ["Designation", "verifyDesignation"], ["Date of Joining", "doj"], ["Salary", "lastSalary"], ["Notice Period", "noticePeriod"]].map(([l, k]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{l}</div>
            {verdict(data.verifications[k] || "true")}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {[data.ref1, data.ref2].map((ref, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: 14, border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{ref.name}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>{ref.role} · {ref.company}</div>
            {[["Behaviour", ref.behaviour], ["Ethical", ref.ethical], ["Professional", ref.professional]].map(([l, v]) => (
              <div key={l} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>{l}</div>
                <div>{stars(v)}</div>
              </div>
            ))}
            <div style={{ marginTop: 8, padding: "6px 8px", background: "rgba(255,255,255,0.04)", borderRadius: 6, fontSize: 10, color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>"{ref.comment}"</div>
            <div style={{ marginTop: 8 }}><Badge color="#10b981">↩ Would Rehire: {ref.rehire}</Badge></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Demo App ─────────────────────────────────────────────
export default function DemoPage() {
  const [activeTab, setActiveTab] = useState("uan");
  const [input, setInput] = useState("101257351470");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(0);
  const resultRef = useRef(null);

  const tabs = [
    { id: "uan", label: "UAN Search", icon: "🏦", input: "101257351470", hint: "UAN Number" },
    { id: "pan", label: "PAN Verify", icon: "📋", input: "AQLPJ2746D", hint: "PAN Number" },
    { id: "dl", label: "Driving License", icon: "🚗", input: "TN2020150000123", hint: "DL Number" },
    { id: "fullbgv", label: "Full BGV ⚡", icon: "⚡", input: "All checks combined", hint: "Auto-filled" },
    { id: "refcheck", label: "Reference Check", icon: "📧", input: "RC-DEMO001", hint: "Check ID" },
  ];

  const steps_map = {
    uan: ["Connecting to EPFO...", "Fetching employment records...", "Building timeline..."],
    pan: ["Verifying PAN number...", "Checking Income Tax DB...", "Confirming Aadhaar link..."],
    dl: ["Connecting to RTO database...", "Verifying license details...", "Checking vehicle classes..."],
    fullbgv: ["Running UAN verification...", "Running PAN verification...", "Running DL check...", "Calculating Meitho Score..."],
    refcheck: ["Sending candidate email...", "Collecting reference responses...", "Compiling verification report..."],
  };

  const handleSearch = () => {
    if (searching) return;
    setResult(null);
    setSearching(true);
    setProgress(0);
    setStep(0);

    const demo = DEMO_PROFILES[activeTab];
    const stepList = steps_map[activeTab];
    const totalTime = demo.delay;
    const stepTime = totalTime / stepList.length;

    stepList.forEach((_, i) => {
      setTimeout(() => setStep(i), i * stepTime);
    });

    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(interval); return 100; }
        return p + (100 / (totalTime / 50));
      });
    }, 50);

    setTimeout(() => {
      setSearching(false);
      setResult(demo.result);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }, totalTime);
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    setResult(null);
    setProgress(0);
    setSearching(false);
    const t = tabs.find(t => t.id === id);
    setInput(t?.input || "");
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#06080f 0%,#0a0d18 100%)", fontFamily: FF, color: "#fff" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.5; } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
      `}</style>

      {/* Header */}
      <div style={{ background: "rgba(10,13,24,0.95)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg,#ff3131,#ff914d)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
          </div>
          <div style={{ fontFamily: FFD, fontWeight: 800, fontSize: 18, color: "#fff" }}>Meitho BGV</div>
          <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 99, background: "rgba(255,145,77,0.15)", border: "1px solid rgba(255,145,77,0.3)", color: "#ff914d", fontWeight: 700 }}>INTERACTIVE DEMO</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/" style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, textDecoration: "none", fontWeight: 600 }}>← Back to Site</a>
          <a href="/" onClick={e=>{e.preventDefault();window.location.href="/";}} style={{ background: "linear-gradient(90deg,#ff3131,#ff914d)", border: "none", borderRadius: 8, color: "#fff", padding: "7px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>Get Started →</a>
        </div>
      </div>

      {/* Hero */}
      <div style={{ textAlign: "center", padding: "60px 24px 40px" }}>
        <div style={{ fontSize: 11, color: "#ff914d", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Live Interactive Demo</div>
        <div style={{ fontFamily: FFD, fontSize: "clamp(28px,5vw,52px)", fontWeight: 900, color: "#fff", lineHeight: 1, marginBottom: 16 }}>
          See Meitho BGV<br />
          <span style={{ background: "linear-gradient(90deg,#0cc0df,#8b5cf6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in Action</span>
        </div>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", maxWidth: 500, margin: "0 auto 8px", lineHeight: 1.7 }}>
          Try a live simulation of our verification platform. No login required — real UI, realistic data.
        </p>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>⚠️ Demo uses simulated data. Real verifications require a company account.</div>
      </div>

      {/* Main Demo Area */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 16px 60px" }}>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => handleTabChange(t.id)} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: activeTab === t.id ? "linear-gradient(90deg,#ff3131,#ff914d)" : "rgba(255,255,255,0.05)", color: activeTab === t.id ? "#fff" : "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: FF, whiteSpace: "nowrap", transition: "all .15s" }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Search Box */}
        <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: 20, border: "1px solid rgba(255,255,255,0.08)", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
            {tabs.find(t => t.id === activeTab)?.hint}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={input} onChange={e => setInput(e.target.value)} readOnly={activeTab === "fullbgv"}
              style={{ flex: 1, padding: "12px 16px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#fff", fontSize: 14, fontFamily: activeTab === "fullbgv" ? FF : FFM, outline: "none", letterSpacing: activeTab === "fullbgv" ? "normal" : 1 }}
              placeholder={tabs.find(t => t.id === activeTab)?.input} />
            <button onClick={handleSearch} disabled={searching} style={{ padding: "12px 24px", background: searching ? "rgba(255,255,255,0.08)" : "linear-gradient(90deg,#ff3131,#ff914d)", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 700, cursor: searching ? "not-allowed" : "pointer", fontFamily: FF, display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", opacity: searching ? 0.6 : 1 }}>
              {searching
                ? <><div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin .8s linear infinite" }} /> Verifying...</>
                : <>{activeTab === "fullbgv" ? "⚡ Run Full BGV" : activeTab === "refcheck" ? "📧 View Report" : "🔍 Search"}</>
              }
            </button>
          </div>

          {/* Progress */}
          {searching && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", animation: "pulse 1s infinite" }}>
                  {steps_map[activeTab][step] || "Processing..."}
                </div>
                <div style={{ fontSize: 12, color: "#0cc0df", fontWeight: 700 }}>{Math.round(progress)}%</div>
              </div>
              <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg,#ff3131,#ff914d,#0cc0df)", borderRadius: 2, transition: "width .1s linear" }} />
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        {result && (
          <div ref={resultRef} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 16, padding: 20, border: "1px solid rgba(255,255,255,0.08)", animation: "fadeUp .4s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 20 }}>{tabs.find(t => t.id === activeTab)?.icon}</span>
                <div>
                  <div style={{ fontFamily: FFD, fontSize: 14, fontWeight: 700, color: "#fff" }}>
                    {tabs.find(t => t.id === activeTab)?.label} — Result
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>Simulated demo data</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Badge color="#10b981">✅ Verified</Badge>
                <Badge color="#8b5cf6">DB Cache</Badge>
              </div>
            </div>

            {activeTab === "uan" && <UANResult data={result} />}
            {activeTab === "pan" && <PANResult data={result} />}
            {activeTab === "dl" && <DLResult data={result} />}
            {activeTab === "fullbgv" && <FullBGVResult data={result} />}
            {activeTab === "refcheck" && <RefCheckResult data={result} />}

            {/* CTA */}
            <div style={{ marginTop: 20, padding: 16, background: "linear-gradient(135deg,rgba(255,49,49,0.08),rgba(255,145,77,0.08))", borderRadius: 12, border: "1px solid rgba(255,49,49,0.15)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 2 }}>Ready to verify real candidates?</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Register your company · Setup in 5 minutes · Pay per use</div>
              </div>
              <a href="/" style={{ background: "linear-gradient(90deg,#ff3131,#ff914d)", border: "none", borderRadius: 10, color: "#fff", padding: "10px 20px", cursor: "pointer", fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>
                Get Started Free →
              </a>
            </div>
          </div>
        )}

        {/* Feature cards */}
        {!result && !searching && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: 8 }} className="demo-grid">
            {[
              { icon: "⚡", title: "Under 3 Minutes", desc: "Full BGV check faster than making a cup of tea" },
              { icon: "🏛️", title: "Government Data", desc: "Direct EPFO, PAN & RTO database integration" },
              { icon: "💰", title: "₹220 Full BGV", desc: "80% cheaper than traditional BGV agencies" },
            ].map((f, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: 16, border: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
                <div style={{ fontFamily: FFD, fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 5 }}>{f.title}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
            <style>{`@media(max-width:600px){.demo-grid{grid-template-columns:1fr!important;}}`}</style>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div style={{ background: "rgba(255,49,49,0.06)", borderTop: "1px solid rgba(255,49,49,0.12)", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: FFD, fontSize: 28, fontWeight: 900, color: "#fff", marginBottom: 10 }}>Start Verifying Today</div>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", marginBottom: 24, lineHeight: 1.7 }}>
          Join 500+ companies that trust Meitho BGV.<br />Setup in 5 minutes · Pay per use · No commitments.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/" style={{ background: "linear-gradient(90deg,#ff3131,#ff914d)", border: "none", borderRadius: 12, color: "#fff", padding: "12px 28px", cursor: "pointer", fontSize: 14, fontWeight: 700, textDecoration: "none", boxShadow: "0 8px 24px rgba(255,49,49,0.35)" }}>
            Register Company →
          </a>
          <a href="mailto:help@meitho.com" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", padding: "12px 28px", cursor: "pointer", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
            Talk to Sales
          </a>
        </div>
        <div style={{ marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.2)" }}>
          meitho.com · help@meitho.com · +91 8300016265
        </div>
      </div>
    </div>
  );
}
